import { useNavigation } from '@react-navigation/native'
import { useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { ScrollView } from 'react-native'
import styled from 'styled-components/native'

import { extractApiErrorMessage } from 'api/apiHelpers'
import { useAuthContext } from 'features/auth/context/AuthContext'
import { useSaveStep } from 'features/identityCheck/pages/helpers/useSaveStep'
import { usePostHonorStatementMutation } from 'features/identityCheck/queries/usePostHonorStatementMutation'
import { IdentityCheckStep } from 'features/identityCheck/types'
import { UseNavigationType } from 'features/navigation/navigators/RootNavigator/types'
import { getSubscriptionHookConfig } from 'features/navigation/navigators/SubscriptionStackNavigator/getSubscriptionHookConfig'
import { QueryKeys } from 'libs/queryKeys'
import { useGetHeaderHeight } from 'shared/header/useGetHeaderHeight'
import { hasOngoingCredit } from 'shared/user/getAvailableCredit'
import { Button } from 'ui/designSystem/Button/Button'
import { showErrorSnackBar } from 'ui/designSystem/Snackbar/snackBar.store'
import { useEnterKeyAction } from 'ui/hooks/useEnterKeyAction'
import { Page } from 'ui/pages/Page'
import { Spacer, Typo, getSpacing } from 'ui/theme'
import { getTextSemanticAttrs } from 'ui/theme/typographyAttrs/getTextSemanticAttrs'

export const IdentityCheckHonor = () => {
  const headerHeight = useGetHeaderHeight()
  const queryClient = useQueryClient()
  const { navigate } = useNavigation<UseNavigationType>()
  const { refetchUser } = useAuthContext()
  const saveStep = useSaveStep()

  const {
    mutate: postHonorStatement,
    isPending: isPostingHonorLoading,
    isSuccess: isPostingHonorSuccess,
  } = usePostHonorStatementMutation({
    onSuccess: async () => {
      void queryClient.invalidateQueries({
        queryKey: [QueryKeys.HOME_BANNER],
      })
      let userProfile
      try {
        const { data: user } = await refetchUser()
        userProfile = user
      } catch (error) {
        showErrorSnackBar(extractApiErrorMessage(error))
      }
      const hasUserOngoingCredit = userProfile ? hasOngoingCredit(userProfile) : false
      if (hasUserOngoingCredit) {
        navigate(...getSubscriptionHookConfig('BeneficiaryAccountCreated'))
      } else {
        void saveStep(IdentityCheckStep.CONFIRMATION)
        navigate(...getSubscriptionHookConfig('BeneficiaryRequestSent'))
      }
    },
    onError: (error) => showErrorSnackBar(extractApiErrorMessage(error)),
  })

  // If the mutation is loading or is a success, we don't want the user to trigger the button again
  const isSubmitButtonEnabled = isPostingHonorLoading || isPostingHonorSuccess
  useEnterKeyAction(() => postHonorStatement())

  return (
    <Page>
      <StyledScrollView>
        <HeaderHeightSpacer headerHeight={headerHeight} />
        <Typo.Title2 {...getTextSemanticAttrs(1)}>
          Les informations que tu as renseignées sont-elles correctes&nbsp;?
        </Typo.Title2>
        <TextContainer>
          <Typo.Title4 {...getTextSemanticAttrs(2)}>
            &quot;Je déclare que l’ensemble des informations que j’ai renseignées durant mon
            inscription sont correctes.&quot;
          </Typo.Title4>
        </TextContainer>
        <StyledBody>
          Des contrôles aléatoires seront effectués et un justificatif de domicile devra être
          fourni. En cas de fraude, des poursuites judiciaires pourraient être engagées.
        </StyledBody>
        <ButtonContainer>
          <Button
            fullWidth
            type="submit"
            onPress={postHonorStatement}
            wording="Valider et continuer"
            isLoading={isSubmitButtonEnabled}
          />
        </ButtonContainer>
        <Spacer.BottomScreen />
      </StyledScrollView>
    </Page>
  )
}
const TextContainer = styled.View(({ theme }) => ({
  marginTop: theme.designSystem.size.spacing.xxxl,
  marginBottom: theme.designSystem.size.spacing.l,
}))

const ButtonContainer = styled.View(({ theme }) => ({
  marginTop: getSpacing(15),
  marginBottom: theme.designSystem.size.spacing.xl,
}))

const StyledScrollView = styled(ScrollView).attrs(({ theme }) => ({
  contentContainerStyle: {
    paddingHorizontal: theme.contentPage.marginHorizontal,
    paddingVertical: theme.contentPage.marginVertical,
    maxWidth: theme.contentPage.maxWidth,
    width: '100%',
    alignSelf: 'center',
  },
}))``

const HeaderHeightSpacer = styled.View.attrs<{ headerHeight: number }>({})<{
  headerHeight: number
}>(({ headerHeight }) => ({
  paddingTop: headerHeight,
}))

const StyledBody = styled(Typo.BodyS)(({ theme }) => ({
  color: theme.designSystem.color.text.subtle,
}))
